import {useMutation, useQuery} from "react-query";
import axios from "axios";
import {getAuthConfig, getValidateConfig} from "../utils";
const auth = getAuthConfig()

const assistantBodyExample = {
    model: "gpt-4o",
    instructions:
        "Instructions...",
    tools: [
        {
            type: "function",
            function: {
                name: "create_supportneed",
                description: "create",
                parameters: {
                    type: "object",
                    properties: {
                        location: {
                            type: "string",
                            description: "The city and state, e.g., San Francisco, CA",
                        },
                        unit: {
                            type: "string",
                            enum: ["Celsius", "Fahrenheit"],
                            description:
                                "The temperature unit to use. Infer this from the user's location.",
                        },
                    },
                    required: ["location", "unit"],
                },
            },
        },
        {
            type: "file_search",
        },
    ],
}

export function GetSupportBotEnabled() {
    return useQuery("supportBotEnabled", async () => {
        const { data } = await axios.get(
            "/api/v2/config.php/supportbot/enabled",
            auth
        )
        return data
    })
}

export function GetBots() {
    return useQuery("bots", async () => {
        const { data } = await axios.get(
            "/api/v2/supportmodule.php",
            auth
        )
        return data
    })
}

export function GetBotWithId(botId) {
    return useQuery("bot"+botId, async () => {
        const { data } = await axios.get(
            "/api/v2/supportmodule/"+botId,
            auth
        )
        return data
    })
}

export function PostBot() {
    return useMutation('botnew',botData => {
        const data = axios.post(
            "/api/v2/supportmodule.php/",
            botData,
            auth
        )
        return data
    })
}

export function PostBotWithId(botId) {
    return useMutation('bot'+botId,botData => {
        const data = axios.post(
            "/api/v2/supportmodule.php/" + botId,
            botData,
            auth
        )
        return data
    })
}

export function GetSupportTopics() {
    return useQuery("supportTopics", async () => {
        const { data } = await axios.get(
            "/api/v2/codes.php/category",
            auth
        )
        return data
    })
}

export function GetAssistantInstructionsConfig() {
    return useQuery("assistantInstructionsConfig", async () => {
        const { data } = await axios.get(
            "/api/v2/config.php/supportbot/instructions",
            auth
        )
        return data
    })
}

export function PostAssistant() {
    return useMutation('assistantnew',postData => {
        const data = axios.post(
            "/api/v2/openai.php/assistants/",
            postData,
            auth
        )
        return data
    })
}

export function PostAssistantWithId(assistantId) {
    return useMutation('assistant'+assistantId,postData => {
        const data = axios.post(
            "/api/v2/openai.php/assistants/" + assistantId,
            postData,
            auth
        )
        return data
    })
}

export function GetAssistant(assistantId, enabled = true) {
    return useQuery(
        "assistant" + assistantId,
        async () => {
            const { data } = await axios.get(
                "/api/v2/openai.php/assistants/" + assistantId,
                auth
            )
            return data;
        },
        {
            enabled: enabled && !!assistantId
        }
    );
}

export function CreateThread() {
    return useMutation("threadnew", async () => {
        const { data } = await axios.post("/api/v2/threads.php", {}, auth);
        return data; // { id: string, ... }
    });
}

export function AddMessageToThread(threadId) {
    return useMutation(
        ["thread.addMessage", threadId],
        async ({ role, content }) => {
            if (!threadId) throw new Error("Missing threadId");
            const { data } = await axios.post(
                `/api/v2/threads.php/${threadId}/messages`,
                { role, content },
                auth
            );
            return data;
        },
        { retry: 1 }
    );
}

export function CreateAndPollThread(threadId) {
    return useMutation(
        ["thread.run", threadId],
        async ({ assistantId }) => {
            if (!threadId) throw new Error("Missing threadId");
            if (!assistantId) throw new Error("Missing assistantId");

            // Create run
            const runRes = await axios.post(
                `/api/v2/threads.php/${threadId}/runs`,
                { assistant_id: assistantId },
                auth
            );
            const runId = runRes?.data?.id;
            if (!runId) throw new Error("Run creation failed");

            const toolEvents = [];
            // Poll
            const maxAttempts = 60;
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                // eslint-disable-next-line no-await-in-loop
                const { data: runData } = await axios.get(
                    `/api/v2/threads.php/${threadId}/runs/${runId}`,
                    auth
                );

                if (runData.status === "completed") {
                    return { runId, status: "completed", toolEvents };
                }

                if (runData.status === "failed") {
                    throw new Error("Run failed");
                }

                if (runData.status === "requires_action") {
                    const toolCalls = runData.required_action?.submit_tool_outputs?.tool_calls || [];
                    // Record tool calls to show in UI (do not actually call external systems)
                    toolCalls.forEach((call) => {
                        try {
                            const args = JSON.parse(call.function?.arguments || "{}");
                            toolEvents.push({
                                name: call.function?.name,
                                args: args,
                                id: call.id
                                });
                        } catch (e) {
                            toolEvents.push({
                                name: call.function?.name || 'unknown',
                                args: {
                                    raw: call.function?.arguments || "[unparseable]",
                                    error: "Failed to parse arguments"
                                },
                                id: call.id
                                });
                        }
                    });

                    // Submit stubbed outputs to progress the run
                    const tool_outputs = toolCalls.map((call) => ({
                        tool_call_id: call.id,
                        output: JSON.stringify({ success: true, note: "Simulated in BotModuleTester" }),
                    }));

                    // eslint-disable-next-line no-await-in-loop
                    await axios.post(
                        `/api/v2/threads.php/${threadId}/runs/${runId}/submit_tool_outputs`,
                        { tool_outputs },
                        auth
                    );
                }

                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 1000));
            }

            throw new Error("Run polling timeout");
        },
        { retry: 0 }
    );
}

export function GetThreadMessages(threadId) {
    return useQuery(
        ["thread.messages", threadId],
        async () => {
            if (!threadId) return { data: { data: [] } };
            const { data } = await axios.get(`/api/v2/threads.php/${threadId}/messages`, auth);
            return data; // { data: Message[] }
        },
        {
            enabled: !!threadId,
            refetchOnWindowFocus: false,
        }
    );
}

export function GetSupportbotInstructionsInstructions() {
    return useQuery("SupportbotInstructionsInstructions", async () => {
        const { data } = await axios.get(
            "/api/v2/translation.php/admin/supportbot.instructions.instructions/",
            auth
        )
        return data
    })
}

export function GetSupportbotInstructionsSupportTopicInstructionBody() {
    return useQuery("SupportbotInstructionsSupportTopicInstructionBody", async () => {
        const { data } = await axios.get(
            "/api/v2/translation.php/admin/supportbot.instructions.supportTopicInstructionBody/",
            auth
        )
        return data
    })
}

export function GetSupportbotInstructionsSupportBotInstructionBody() {
    return useQuery("SupportbotInstructionsSupportBotInstructionBody", async () => {
        const { data } = await axios.get(
            "/api/v2/translation.php/admin/supportbot.instructions.supportBotInstructionBody/",
            auth
        )
        return data
    })
}

// OpenAI Files API functions
export function UploadFileToOpenAI() {
    return useMutation('uploadFile', async (fileData) => {
        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('purpose', 'assistants');

        // Use the existing getValidateConfig() which handles multipart/form-data and auth
        const config = getValidateConfig();

        console.log('Uploading file:', fileData.file.name, 'Size:', fileData.file.size, 'Type:', fileData.file.type);

        const { data } = await axios.post(
            "/api/v2/openai.php/files",
            formData,
            config
        );
        return data;
    });
}

export function DeleteFileFromOpenAI() {
    return useMutation('deleteFile', async (fileId) => {
        const { data } = await axios.delete(
            `/api/v2/openai.php/files/${fileId}`,
            auth
        );
        return data;
    });
}

export function GetFileMetadata(fileId) {
    return useQuery(['fileMetadata', fileId], async () => {
        const { data } = await axios.get(
            `/api/v2/openai.php/files/${fileId}`,
            auth
        );
        return data;
    }, {
        enabled: false  // Manual trigger only
    });
}

export function CreateVectorStore() {
    return useMutation('createVectorStore', async (storeData) => {
        const { data } = await axios.post(
            `/api/v2/openai.php/vector_stores`,
            storeData,
            auth
        );
        return data;
    });
}



export function GetVectorStoreFiles(vectorStoreId, enabled) {
    return useQuery(['vectorStoreFiles', vectorStoreId], async () => {
        const { data } = await axios.get(
            `/api/v2/openai.php/vector_stores/${vectorStoreId}/files`,
            auth
        );
        return data;
    }, {
        enabled: enabled && !!vectorStoreId
    });
}



export function AddFileToVectorStore() {
    return useMutation('addFileToVectorStore', async ({ vectorStoreId, fileId }) => {
        const { data } = await axios.post(
            `/api/v2/openai.php/vector_stores/${vectorStoreId}/files`,
            { file_id: fileId },
            auth
        );
        return data;
    });
}

export function RemoveFileFromVectorStore() {
    return useMutation('removeFileFromVectorStore', async ({ vectorStoreId, fileId }) => {
        const { data } = await axios.delete(
            `/api/v2/openai.php/vector_stores/${vectorStoreId}/files/${fileId}`,
            auth
        );
        return data;

    });

}